import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { MdClose, MdShoppingCart } from 'react-icons/md';
import { FaPepperHot, FaPlus, FaMinus } from 'react-icons/fa';
import { motion, AnimatePresence, Variants, useReducedMotion } from 'framer-motion';
import { MenuItem } from '../redux/slices/menuSlice';
import { VegDotIcon, DietaryOverlayIcon } from './';
import {
  filterEnabledModifiers,
  handleModifierOptionSelection,
  isModifierOptionSelected,
  isModifierRequired,
} from '../utils/modifierUtils';
import { getPrimaryDietaryType } from '../utils/dietaryUtils';
import { calculateTotalPrice, formatPrice } from '../utils/priceUtils';
import {
  shouldShowSpiceLevel,
  SPICE_LEVELS,
  DEFAULT_SPICE_LEVEL,
} from '../utils/spiceUtils';
import { validateAllSelections, type ValidationErrors } from '../utils/validationUtils';
import {
  NutritionalInfo,
  IngredientsSection,
  AllergensSection,
  RecommendedProducts,
  ModifierGroup,
  type SelectedModifier,
} from './product-details';

// ============================================
// CONSTANTS
// ============================================
const FOOTER_HEIGHT = 80;
const STICKY_HEADER_HEIGHT = 60;

// ============================================
// ANIMATION VARIANTS (Best Practice #5)
// ============================================
const overlayVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 },
};

const stickyHeaderVariants: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

// Spring transition for natural motion (Best Practice #6)
const springTransition = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
};

// ============================================
// TYPES
// ============================================
interface ModifierOption {
  id: string | number;
  name: string;
  price: number | string;
}

interface ItemDetailsModalProps {
  isOpen: boolean;
  item: MenuItem | null;
  onClose: () => void;
  onAddToCart?: (
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: SelectedModifier[]
  ) => Promise<void> | void;
  menuData?: MenuItem[] | null;
  onRecommendedItemClick?: (item: MenuItem) => void;
}

// ============================================
// COMPONENT
// ============================================
export const ItemDetailsModal: React.FC<ItemDetailsModalProps> = React.memo(
  ({ isOpen, item, onClose, onAddToCart, menuData, onRecommendedItemClick }) => {
    // Accessibility: Respect reduced motion preference
    const shouldReduceMotion = useReducedMotion();
    const animationTransition = shouldReduceMotion ? { duration: 0 } : springTransition;

    const [quantity, setQuantity] = useState(1);
    const [spiceLevel, setSpiceLevel] = useState(DEFAULT_SPICE_LEVEL);
    const [selectedModifiers, setSelectedModifiers] = useState<SelectedModifier[]>([]);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showStickyHeader, setShowStickyHeader] = useState(false);
    const [expandedModifiers, setExpandedModifiers] = useState<Set<string>>(new Set());

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    // Modifiers list
    const modifiersList = useMemo(
      () => (item ? filterEnabledModifiers(item.modifiers_list || []) : []),
      [item?.modifiers_list]
    );

    // Get ALL required modifier names for auto-expand
    const requiredModifiers = useMemo(() => {
      return modifiersList
        .filter(mod => isModifierRequired(mod))
        .map(mod => mod.name);
    }, [modifiersList]);

    // Reset when modal opens
    useEffect(() => {
      if (isOpen && item) {
        setQuantity(1);
        setSpiceLevel(DEFAULT_SPICE_LEVEL);
        setSelectedModifiers([]);
        setValidationErrors({});
        setIsAddingToCart(false);
        setError(null);
        setShowStickyHeader(false);
        // Auto-expand all required modifiers
        setExpandedModifiers(new Set(requiredModifiers));
      }
    }, [isOpen, item, requiredModifiers]);

    // Handle scroll for sticky header (Best Practice #9: passive listener)
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container || !isOpen) return;

      const handleScroll = () => {
        const scrollTop = container.scrollTop;
        const imageHeight = imageRef.current?.offsetHeight || 200;
        setShowStickyHeader(scrollTop > imageHeight - STICKY_HEADER_HEIGHT);
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }, [isOpen]);

    // ESC key close
    useEffect(() => {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'hidden';
        return () => {
          document.removeEventListener('keydown', handleEscKey);
          document.body.style.overflow = '';
        };
      }
    }, [isOpen, onClose]);

    // ============================================
    // CENTRALIZED VALIDATION (Best Practice #1)
    // Compute once, reuse everywhere
    // ============================================
    const validationResult = useMemo(() => {
      if (!item) return { isValid: false, errors: {} as ValidationErrors };
      return validateAllSelections(item, modifiersList, selectedModifiers, spiceLevel);
    }, [item, modifiersList, selectedModifiers, spiceLevel]);

  const isFormValid = validationResult.isValid;

  // Determine if item needs customization (spice or modifiers)
  const needsCustomization = useMemo(
    () => (item ? shouldShowSpiceLevel(item) || modifiersList.length > 0 : false),
    [item, modifiersList]
  );

  const handleModifierOptionSelect = useCallback(
      (modifierName: string, option: ModifierOption) => {
        setSelectedModifiers((prev) =>
          handleModifierOptionSelection(modifierName, option, prev, modifiersList)
        );
        // Clear validation error for this modifier
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[modifierName];
          return newErrors;
        });
      },
      [modifiersList]
    );

    const isModifierOptionSelectedLocal = useCallback(
      (modifierName: string, optionName: string): boolean =>
        isModifierOptionSelected(modifierName, optionName, selectedModifiers),
      [selectedModifiers]
    );

    const totalPrice = useMemo(
      () => (item ? calculateTotalPrice(item.price, selectedModifiers, quantity) : 0),
      [item, selectedModifiers, quantity]
    );

    const handleAddToCartClick = useCallback(async () => {
      if (!item || !validationResult.isValid || !onAddToCart) {
        // Show validation errors from centralized result
        setValidationErrors(validationResult.errors);
        return;
      }

      setIsAddingToCart(true);
      setError(null);

      try {
        const finalSpiceLevel = shouldShowSpiceLevel(item) ? spiceLevel : null;
        await onAddToCart(item, quantity, finalSpiceLevel, selectedModifiers);
        onClose();
      } catch (err) {
        console.error('Add to order failed:', err);
        setError('Failed to add item to order. Please try again.');
      } finally {
        setIsAddingToCart(false);
      }
    }, [item, quantity, spiceLevel, selectedModifiers, onAddToCart, onClose, validationResult]);

    const handleImageError = useCallback(
      (e: React.SyntheticEvent<HTMLImageElement>) => {
        (e.target as HTMLImageElement).src = `https://via.placeholder.com/600x400/f8f9fa/6c757d?text=${encodeURIComponent(
          item?.name || 'Item'
        )}`;
      },
      [item?.name]
    );

    const handleRecommendedClick = useCallback(
      (newItem: MenuItem) => {
        if (onRecommendedItemClick) {
          onRecommendedItemClick(newItem);
        }
      },
      [onRecommendedItemClick]
    );

    // Derived state
    const isOutOfStock = item ? !item.inventory_status : false;
    const dietaryType = item ? getPrimaryDietaryType(item) : null;
    const showVegDot = dietaryType === 'vegetarian' || dietaryType === 'vegan';

    if (!isOpen || !item) return null;

    return (
      <AnimatePresence>
        <motion.div
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[99999] p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={onClose}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={animationTransition}
            className="bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-lg md:max-w-xl lg:max-w-lg sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sticky Header - Shows on scroll */}
            <AnimatePresence>
              {showStickyHeader && (
                <motion.div
                  variants={stickyHeaderVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  transition={animationTransition}
                  className="absolute top-0 left-0 right-0 z-20 bg-white border-b border-gray-200 px-4 h-[60px] flex items-center gap-3 shadow-sm"
                >
                  {/* Small Product Image - Only show if image exists */}
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name}
                      loading="lazy"
                      className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                      onError={handleImageError}
                    />
                  )}
                  <h3 className="font-semibold text-gray-900 truncate text-left flex-1">
                    {item.name}
                  </h3>
                  {/* Close button on right */}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                    aria-label="Close modal"
                  >
                    <MdClose className="w-5 h-5 text-gray-600" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Scrollable Content */}
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto overscroll-contain"
              style={{ paddingBottom: onAddToCart ? `${FOOTER_HEIGHT}px` : '0px' }}
            >
              {/* Image Section - Only show if image exists */}
              {item.image && (
                <div ref={imageRef} className="relative">
                  <img
                    src={item.image}
                    alt={item.name}
                    loading="lazy"
                    className="w-full h-40 sm:h-44 md:h-48 object-cover"
                    onError={handleImageError}
                  />

                  {/* Close button on image */}
                  <button
                    onClick={onClose}
                    className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors"
                    aria-label="Close modal"
                  >
                    <MdClose className="w-5 h-5 text-gray-700" />
                  </button>

                  {/* Dietary Icons */}
                  <div className="absolute top-3 left-3">
                    <DietaryOverlayIcon item={item} size="lg" />
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="p-4 space-y-5">
                {/* Item Header */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {showVegDot && <VegDotIcon item={item} size="md" />}
                    <h2
                      id="modal-title"
                      className="text-xl sm:text-2xl font-bold text-gray-900 flex-1"
                    >
                      {item.name}
                    </h2>
                    {/* Close button when no image */}
                    {!item.image && (
                      <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                        aria-label="Close modal"
                      >
                        <MdClose className="w-5 h-5 text-gray-700" />
                      </button>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-600 text-sm font-medium">{error}</p>
                  </div>
                )}

                {/* Customize Section */}
                {(shouldShowSpiceLevel(item) || modifiersList.length > 0) && (
                  <div className="space-y-5">
                    {/* Spice Level */}
                    {shouldShowSpiceLevel(item) && (
                      <div className="mb-5">
                        <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                          Spice Level
                          <span
                            className="text-xs px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: '#FB2B37', color: 'white' }}
                          >
                            Required
                          </span>
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                          {SPICE_LEVELS.map((level) => (
                            <button
                              key={level.id}
                              onClick={() => setSpiceLevel(level.id)}
                              className={`p-3 rounded-lg border text-center transition-all ${
                                spiceLevel === level.id
                                  ? 'border-2 bg-red-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{
                                borderColor:
                                  spiceLevel === level.id ? '#FB2B37' : undefined,
                              }}
                              aria-pressed={spiceLevel === level.id}
                            >
                              <div className="flex items-center justify-center">
                                {Array.from({ length: level.pepperCount }).map((_, i) => (
                                  <FaPepperHot
                                    key={`pepper-${level.id}-${i}`}
                                    className="h-4 w-4 mx-0.5"
                                    style={{ color: '#FB2B37' }}
                                  />
                                ))}
                              </div>
                            </button>
                          ))}
                        </div>
                        {validationErrors['Spice Level'] && (
                          <p className="text-red-500 text-xs mt-1 font-medium" role="alert">
                            {validationErrors['Spice Level']}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Modifiers - Collapsible Groups */}
                    {modifiersList.map((modifier) => (
                      <ModifierGroup
                        key={modifier.id ?? modifier.name}
                        modifier={modifier}
                        isExpanded={expandedModifiers.has(modifier.name)}
                        onToggle={() =>
                          setExpandedModifiers((prev) => {
                            const newSet = new Set(prev);
                            if (newSet.has(modifier.name)) {
                              newSet.delete(modifier.name);
                            } else {
                              newSet.add(modifier.name);
                            }
                            return newSet;
                          })
                        }
                        onOptionSelect={(option) =>
                          handleModifierOptionSelect(modifier.name, option)
                        }
                        isOptionSelected={(optionName) =>
                          isModifierOptionSelectedLocal(modifier.name, optionName)
                        }
                        validationError={validationErrors[modifier.name]}
                      />
                    ))}
                  </div>
                )}

                {/* You May Also Like - Always show after modifiers */}
                {menuData && menuData.length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <RecommendedProducts
                      currentProductId={item.pk_id?.toString() || ''}
                      allItems={menuData}
                      product={item}
                      showPrice={true}
                      onItemClick={handleRecommendedClick}
                      forceHorizontal={true}
                    />
                  </div>
                )}

                {/* Collapsible Info Sections */}
                <div className="space-y-2">
                  <NutritionalInfo product={item} />
                  <IngredientsSection product={item} />
                  <AllergensSection product={item} />
                </div>
              </div>
            </div>

            {/* Fixed Footer (Best Practice #3: iOS Safe Area) - Only show if onAddToCart is provided */}
            {onAddToCart && (
              <div
                className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg"
                style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
              >
                <div className="flex items-center gap-3">
                  {/* Quantity Controls */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      className="w-8 h-8 rounded-md bg-white flex items-center justify-center transition-all disabled:opacity-50"
                      aria-label="Decrease quantity"
                    >
                      <FaMinus className="w-3 h-3 text-gray-600" />
                    </button>
                    <span className="w-10 text-center font-bold text-gray-900">{quantity}</span>
                    <button
                      onClick={() => setQuantity(q => q + 1)}
                      className="w-8 h-8 rounded-md bg-white flex items-center justify-center transition-all"
                      aria-label="Increase quantity"
                    >
                      <FaPlus className="w-3 h-3 text-gray-600" />
                    </button>
                  </div>

                  {/* Add to Cart Button */}
                  <button
                    onClick={handleAddToCartClick}
                    disabled={isAddingToCart || isOutOfStock || !isFormValid}
                    className={`flex-1 font-semibold py-3.5 px-4 rounded-xl transition-all duration-200 flex items-center justify-between ${
                      isAddingToCart || isOutOfStock || !isFormValid
                        ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                        : 'text-white shadow-lg hover:shadow-xl'
                    }`}
                    style={{
                      backgroundColor:
                        isAddingToCart || isOutOfStock || !isFormValid ? undefined : '#FB2B37',
                    }}
                    aria-label={`Add ${item.name} to order for ${formatPrice(totalPrice)}`}
                  >
                    {isAddingToCart ? (
                      <div className="flex items-center justify-center w-full">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                        Adding...
                      </div>
                    ) : isOutOfStock ? (
                      <div className="flex items-center justify-center w-full">
                        <MdShoppingCart className="w-5 h-5 mr-2" />
                        Out of Stock
                      </div>
                    ) : (
                      <>
                        <span className="flex items-center">
                          <MdShoppingCart className="w-5 h-5 mr-2" />
                          Add to Cart
                        </span>
                        <span className="font-bold">{formatPrice(totalPrice)}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }
);

ItemDetailsModal.displayName = 'ItemDetailsModal';

export default ItemDetailsModal;
